import { useEffect, useLayoutEffect, useMemo, useRef, useState, type CSSProperties, type ReactNode } from 'react'
import { History, Plus } from 'lucide-react'
import { Button, IconButton, Skeleton, useToast } from '../../ui'
import { EventSheet } from '../EventSheet'
import { ScreenHead } from '../ScreenHead'
import { Bubble, MessageMenu, type MenuAction } from './Bubble'
import { Cards } from './Cards'
import { Composer } from './Composer'
import { Choices, isChoice, Proposal, type ChoiceAction } from './Proposal'
import { Sources } from './Sources'
import { Status } from './Status'
import { Thinking } from './Thinking'
import { Trail } from './Trail'
import { useAsk, useCardsById, useThread, withdrawExchange, type CardsById, type Outcome } from '../../lib/chat'
import { lastExchange, stampFor, threadTitle, userSaid, type TrailStep } from '../../lib/chatClient'
import { threadTitle as titleFor } from '../../../../src/butler/title'
import { Failed } from '../../screens/Stub'
import { rowTitle } from '../../lib/rowMeta'
import { supabase } from '../../lib/supabase'
import { useShowings } from '../../lib/queries'
import type { MessagePart } from '../../lib/chatProtocol'
import type { ShowingLite } from '../CardBody'
import type { FeedRow } from '../../lib/types'

/** How close to the foot of the page counts as "reading the latest", so a
 *  streaming answer keeps the page pinned to it — and stops doing so the
 *  moment Doug scrolls up to read something older. */
const STICK_PX = 120

/** The keys the turn drawn from local state goes by, before it has rows. */
const LIVE_Q = 'live:question'
const LIVE_A = 'live:answer'

/** Every entry id a list of parts names — the cards, and the events a
 *  proposal is about — so one `useCardsById` answers the whole thread. */
function idsIn(parts: MessagePart[], into: string[]) {
  for (const p of parts) {
    if (p.type === 'cards') into.push(...p.entry_ids)
    else if (p.type === 'proposal' && 'entry_id' in p.action && p.action.entry_id) into.push(p.action.entry_id)
  }
}

/** The butler's words in a message, for Copy: its text parts, a blank line
 *  between them, as they were drawn. */
const saidIn = (parts: readonly MessagePart[]) =>
  parts.flatMap(p => (p.type === 'text' ? [p.text.trim()] : [])).filter(Boolean).join('\n\n')

/** A persisted assistant row with no parts is an answer Edit or Retry took
 *  back (`withdrawExchange` empties it rather than deleting it, to keep its
 *  cost on the ledger). It draws nothing — not an empty gap in the log. */
const drawable = (m: { role: string; parts: MessagePart[] | null }) => m.role === 'user' || (m.parts?.length ?? 0) > 0

/**
 * One message's parts, in order. Doug's text is a bubble; the butler's text
 * is the page talking. Proposals — every chip in the message — gather into
 * one wrapping row where the first of them fell, each a `Proposal` chip
 * that runs its own mutation on a tap (9.4).
 */
function Parts({
  role, parts, rows, byEntry, outcomes, onOutcome, onOpen, caret = false, onHold,
  thought = null, answering = true, showTrail = true, trail, choicesOpen = false, onChoose,
}: {
  role: 'user' | 'assistant'
  parts: MessagePart[]
  rows: CardsById
  byEntry: Map<string, ShowingLite[]> | undefined
  /** Outcomes tapped this session, overriding the persisted `outcome` on a
   *  part until a later turn's `done` invalidates the thread and the
   *  persisted row agrees (`Thread`'s own note on why). */
  outcomes: Map<string, 'done' | 'declined'>
  onOutcome: (id: string, result: 'done' | 'declined') => void
  onOpen: (e: FeedRow) => void
  /** WP 9.12 — the streaming caret, after the last word while text is the
   *  last thing to have arrived. */
  caret?: boolean
  /** WP 9.12 — a long press on this message's text opens its menu. */
  onHold?: () => void
  /** WP 9.12 — the thinking summary this turn streamed (never persisted:
   *  only a turn from this session has one), and whether its answer has
   *  started, which folds it to one line. */
  thought?: string | null
  answering?: boolean
  /** WP 9.12 — the trail is drawn once the turn is over; while it runs,
   *  the status line says the same thing live. */
  showTrail?: boolean
  /** WP 9.12 — the live turn's trail, folded from its statuses; a
   *  persisted row carries its own `trail` part instead. */
  trail?: TrailStep[]
  /** WP 9.12 — this is the last message and nothing has been sent since,
   *  so its question's options and its follow-ups are still open. */
  choicesOpen?: boolean
  onChoose?: (id: string, text: string) => void
}) {
  // Every title `rows` already carries — the same source `Cards` draws
  // from, so a chip never disagrees with the card above it about what an
  // event is called.
  const titles = useMemo(() => {
    const m = new Map<string, string>()
    for (const [id, e] of rows) m.set(id, rowTitle(e))
    return m
  }, [rows])

  const chips = parts.filter((p): p is Extract<MessagePart, { type: 'proposal' }> =>
    p.type === 'proposal' && !isChoice(p.action))
  let chipsDrawn = false
  const out: ReactNode[] = []
  // What the turn did, above what it said: the thinking, then the trail —
  // wherever the trail part sits in `parts`, it reads as the turn's
  // preamble, the way a classic chat puts its tool use above the answer.
  if (thought) out.push(<Thinking key="thinking" text={thought} answering={answering} />)
  if (showTrail) {
    const steps = trail ?? parts.find((p): p is Extract<MessagePart, { type: 'trail' }> => p.type === 'trail')?.steps
    if (steps?.length) out.push(<Trail key="trail" steps={steps} />)
  }
  const lastIndex = parts.length - 1
  parts.forEach((p, i) => {
    switch (p.type) {
      case 'text':
        out.push(
          <Bubble key={i} role={role} text={p.text} caret={caret && i === lastIndex} onHold={onHold} />,
        )
        break
      case 'cards':
        out.push(<Cards key={i} ids={p.entry_ids} titles={p.titles} firsts={p.firsts} rows={rows} byEntry={byEntry} onOpen={onOpen} />)
        break
      case 'sources':
        out.push(<Sources key={i} items={p.items} />)
        break
      case 'error':
        // A turn the Worker persisted with its error (9.1): history, so a
        // quiet line rather than a banner that would announce itself on
        // every load.
        out.push(<p key={i} className="chat-note">Stopped: {p.message}</p>)
        break
      case 'trail':
        break
      case 'proposal':
        if (isChoice(p.action)) {
          if (choicesOpen && onChoose && (outcomes.get(p.id) ?? p.outcome) === 'offered') {
            out.push(<Choices key={i} id={p.id} action={p.action} said={saidIn(parts)} onChoose={onChoose} />)
          }
          break
        }
        if (chipsDrawn) break
        chipsDrawn = true
        out.push(
          <div key={i} className="chat-chips">
            {chips.map(c => (
              <Proposal
                key={c.id}
                id={c.id}
                action={c.action}
                outcome={outcomes.get(c.id) ?? c.outcome}
                titles={titles}
                rows={rows}
                byEntry={byEntry}
                onOutcome={onOutcome}
              />
            ))}
          </div>,
        )
        break
    }
  })
  return <>{out}</>
}

/**
 * The thread: a header, the persisted messages, then the turn in flight,
 * then the composer fixed above the tab bar. Newest at the bottom; the
 * document scrolls, as on every tab.
 *
 * Keyed by `Butler` on each new or picked thread, so a switch is a fresh
 * `useAsk` — and its unmount aborts whatever was streaming into the old one.
 *
 * WP 9.12's chat manners live here: a long press on a message's text opens
 * its menu — the time, Copy, **Retry** on the butler's last answer (asks
 * Doug's last message again) and **Edit** on Doug's last message (its words
 * back in the composer). Both take the old exchange out of the thread first
 * (`withdrawExchange`), and hide it at once rather than after the round
 * trip. The time is otherwise only on hover, never inline.
 */
export function Thread({ threadId, title, loading, listError = null, fresh = false, onNew, onThreads, onThreadId, seed, starters }: {
  threadId: string | null
  /** `chat_threads.title`, when the Worker has written one. */
  title: string | null
  /** The thread list is still out, so which thread is today's is unknown. */
  loading: boolean
  /** a2-04 — the thread list could not be read, so which thread is today's
   *  is unknown: said, rather than drawn as an empty thread. */
  listError?: Error | null
  /** a2-02 — `threadId` is `null` because Doug asked for a new thread, not
   *  because none was used today. On the wire `null` means "today's
   *  thread", so the first question here makes the thread itself. */
  fresh?: boolean
  onNew: () => void
  onThreads: () => void
  /** The Worker named the thread this turn went to (`x-thread-id`). */
  onThreadId: (id: string) => void
  /** Words to put in the composer, not send. Fills it whenever it changes;
   *  the phone passes none. */
  seed?: string
  /** a3-26 — questions offered on an empty thread, each sent as it is
   *  pressed. The desk's Ask panel passes three; the phone passes none
   *  (Doug, 29 Sept: nothing above its composer). */
  starters?: readonly string[]
}) {
  const threadQ = useThread(threadId)
  const chat = useAsk(threadId)
  const toast = useToast()
  const byEntry = useShowings().data?.byEntry
  const [open, setOpen] = useState<FeedRow | null>(null)
  const [composerH, setComposerH] = useState(0)

  // Rows Edit or Retry took back: hidden at once, before the round trip
  // that removes them (and kept hidden if a refetch races it).
  const [withdrawn, setWithdrawn] = useState<ReadonlySet<string>>(new Set())
  const hide = (ids: readonly string[]) => { if (ids.length) setWithdrawn(prev => new Set([...prev, ...ids])) }
  const unhide = (ids: readonly string[]) =>
    setWithdrawn(prev => new Set([...prev].filter(id => !ids.includes(id))))
  const messages = useMemo(
    () => (threadQ.data ?? []).filter(m => !withdrawn.has(m.id) && drawable(m)),
    [threadQ.data, withdrawn],
  )

  // Which message's menu is open (a row id, or `LIVE_Q`/`LIVE_A`), and the
  // words Edit hands back to the composer.
  const [menu, setMenu] = useState<string | null>(null)
  const [fill, setFill] = useState<{ text: string } | null>(null)
  useEffect(() => { if (seed) setFill({ text: seed }) }, [seed])

  // WP 9.12 — thinking is never persisted, but a turn from this session
  // keeps its folded "Thought for a moment" beside the row it became, so the
  // line does not vanish the instant the refetch replaces the local turn.
  // A reload shows nothing for it, as the brief has it.
  const [thoughts, setThoughts] = useState<ReadonlyMap<string, string>>(new Map())
  useEffect(() => {
    const id = chat.doneId
    const text = chat.thinking
    if (id && text.trim()) setThoughts(prev => (prev.get(id) === text ? prev : new Map(prev).set(id, text)))
  }, [chat.doneId, chat.thinking])

  // 9.1's own report: the app does not patch `chat_messages.parts` itself —
  // the outcome travels with Doug's *next* message (`useAsk`'s own
  // `outcomes` param), and `persist.ts` writes it onto the assistant row
  // once that turn's `done` lands. Until then this local record is what
  // draws a tapped chip as done or declined; a reload before the next
  // message reverts to whatever is persisted (`offered`), which is the one
  // gap that report accepts. `pending` is what the next `ask` sends and is
  // cleared once sent; `overrides` is kept for the life of the thread so
  // the chip stays drawn correctly while the refetch that will agree with
  // it is still in flight.
  const [overrides, setOverrides] = useState<Map<string, 'done' | 'declined'>>(new Map())
  const [pendingOutcomes, setPendingOutcomes] = useState<Outcome[]>([])
  useEffect(() => { setOverrides(new Map()); setPendingOutcomes([]) }, [threadId])
  const onOutcome = (id: string, result: 'done' | 'declined') => {
    setOverrides(prev => new Map(prev).set(id, result))
    setPendingOutcomes(prev => [...prev.filter(o => o.proposal_id !== id), { proposal_id: id, result }])
  }

  useEffect(() => {
    if (chat.threadId && chat.threadId !== threadId) onThreadId(chat.threadId)
  }, [chat.threadId, threadId, onThreadId])

  const ids = useMemo(() => {
    const all: string[] = []
    for (const m of messages) idsIn(m.parts ?? [], all)
    idsIn(chat.live, all)
    return all
  }, [messages, chat.live])
  const rows = useCardsById(ids)

  // Pin to the foot while reading the latest. Only when this pane is the one
  // on screen: X7 keeps every tab mounted, and scrolling the window from a
  // hidden pane would scroll whichever tab Doug is actually looking at.
  const col = useRef<HTMLDivElement>(null)
  const stick = useRef(true)
  const visible = () => !!col.current && col.current.offsetParent !== null

  // The question as sent, for Ask again and Try again: re-sends the same
  // text, which the composer has already cleared.
  const lastAsked = useRef<string | null>(null)
  // a2-02 — the thread a question in a fresh thread made, and whether that
  // insert is still out (two quick sends would otherwise make two).
  const made = useRef<string | null>(null)
  const making = useRef(false)
  const send = async (text: string, chosen?: Outcome) => {
    if (making.current) return
    let target: string | undefined
    if (fresh && threadId === null && !chat.threadId) {
      target = made.current ?? undefined
      if (!target) {
        making.current = true
        const { data, error } = await supabase.from('chat_threads')
          .insert({ title: titleFor(text) }).select('id').single()
        making.current = false
        if (error || !data?.id) {
          // Nothing was asked: the words go back in the composer.
          setFill({ text })
          toast({ text: "Couldn't start a new thread. Try again." })
          return
        }
        made.current = target = data.id
      }
    }
    lastAsked.current = text
    stick.current = true
    setMenu(null)
    // WP 9.12 — a question still open when Doug sends anything is answered
    // or passed over by it: typed words answer a question that allows them
    // (the loop reads them as `[Doug answered: …]`), and pass over one that
    // does not. A tapped option says so itself (`chosen`). Untapped
    // follow-ups report nothing — they were an offer, and simply go.
    const passed: Outcome[] = openChoices
      .filter(c => c.action.kind === 'ask' && c.id !== chosen?.proposal_id)
      .map(c => ({
        proposal_id: c.id,
        result: c.action.kind === 'ask' && c.action.allow_free_text ? 'done' as const : 'declined' as const,
      }))
    const said = [...passed, ...(chosen ? [chosen] : [])]
    const saidIds = new Set(said.map(o => o.proposal_id))
    if (said.length) setOverrides(prev => { const m = new Map(prev); for (const o of said) m.set(o.proposal_id, o.result); return m })
    void chat.ask(text, [...pendingOutcomes.filter(o => !saidIds.has(o.proposal_id)), ...said], target)
    setPendingOutcomes([])
  }
  const choose = (id: string, text: string) => void send(text, { proposal_id: id, result: 'done' })
  const again = () => { if (lastAsked.current) void send(lastAsked.current) }

  // WP 9.2's report: `thread_id: null` means "today's thread" on the wire,
  // so *New* cannot ask for a fresh one by id. *New* used to insert the row
  // the moment it was tapped — every tap a row, two quick taps two, the
  // insert's error never read — and the Threads sheet filled with empty
  // "Untitled" lines (a2-02). Now *New* only remounts onto an empty
  // composer (`fresh`), and `send` makes the thread, titled by the same
  // rule the Worker uses, when the first question goes. RLS fills
  // `user_id`.
  useEffect(() => {
    const on = () => {
      if (!visible()) return
      const left = document.documentElement.scrollHeight - (window.scrollY + window.innerHeight)
      stick.current = left < STICK_PX
    }
    window.addEventListener('scroll', on, { passive: true })
    return () => window.removeEventListener('scroll', on)
  }, [])
  useLayoutEffect(() => {
    if (stick.current && visible()) window.scrollTo(0, document.documentElement.scrollHeight)
  }, [messages, chat.live, chat.asked, chat.status, chat.error, chat.thinking, menu, composerH])

  // ---- Edit and Retry --------------------------------------------------

  // The exchange they act on: the one drawn from local state while there is
  // one (`asked` stays until the refetch has the turn's rows), else the last
  // persisted pair.
  const live = chat.asked !== null
  const liveAnswerShown = chat.live.length > 0 || !!chat.status || !!chat.error || !!chat.thinking.trim()
  const persisted = lastExchange(messages)

  // The message whose question and follow-ups are still open: the last one,
  // if it is the butler's, nothing has been sent since, and no turn is
  // running. `openKey` names it; `openChoices` are its offered choices.
  const lastRow = messages[messages.length - 1]
  const openKey = chat.streaming ? null
    : live ? (liveAnswerShown ? LIVE_A : null)
    : lastRow?.role === 'assistant' ? lastRow.id : null
  const openParts = openKey === LIVE_A ? chat.live : openKey ? lastRow?.parts ?? [] : []
  const openChoices = openParts.flatMap(p =>
    p.type === 'proposal' && isChoice(p.action) && (overrides.get(p.id) ?? p.outcome) === 'offered'
      ? [{ id: p.id, action: p.action as ChoiceAction }] : [])
  const answerable = openChoices.some(c => c.action.kind === 'ask' && c.action.allow_free_text)
  const lastQuestion = live
    ? { key: LIVE_Q, text: chat.asked!, ids: [] as string[] }
    : persisted.question && { key: persisted.question.id, text: userSaid(persisted.question.parts), ids: [persisted.question.id] }
  const lastAnswerKey = live ? (liveAnswerShown ? LIVE_A : null) : persisted.answer?.id ?? null
  const exchangeIds = lastQuestion ? [...lastQuestion.ids, ...(persisted.answer && !live ? [persisted.answer.id] : [])] : []

  // Take the exchange out: at once on screen, then in the table. The ids a
  // turn drawn from local state has only exist in a fresh read, which is
  // what `withdrawExchange` makes.
  const withdraw = async (text: string): Promise<boolean> => {
    hide(exchangeIds)
    const tid = threadId ?? chat.threadId
    if (!tid) return true
    try {
      hide(await withdrawExchange(tid, text))
      return true
    } catch {
      unhide(exchangeIds)
      return false
    }
  }

  const edit = (text: string) => {
    if (chat.streaming) return
    setFill({ text })
    if (live) chat.clear()
    void withdraw(text).then(ok => { if (!ok) toast({ text: 'Could not take that message back.' }) })
  }

  const retry = async (text: string) => {
    if (chat.streaming) return
    // Asked again whether or not the old pair could be removed: Doug wants
    // an answer, and a question drawn twice is the lesser fault.
    await withdraw(text)
    void send(text)
  }

  const copy = (text: string) => {
    void navigator.clipboard?.writeText(text).then(
      () => toast({ text: 'Copied' }),
      () => toast({ text: 'Could not copy.' }),
    )
  }

  const now = new Date()
  const menuFor = (key: string, role: 'user' | 'assistant', parts: MessagePart[], at: string | null) => {
    if (menu !== key) return null
    const actions: MenuAction[] = []
    if (!chat.streaming && lastQuestion) {
      if (role === 'assistant' && key === lastAnswerKey) actions.push({ label: 'Retry', onClick: () => void retry(lastQuestion.text) })
      if (role === 'user' && key === lastQuestion.key) actions.push({ label: 'Edit', onClick: () => edit(lastQuestion.text) })
    }
    const text = role === 'user' ? userSaid(parts) : saidIn(parts)
    if (text) actions.push({ label: 'Copy', onClick: () => copy(text) })
    return (
      <MessageMenu
        stamp={at ? stampFor(at, now) : null}
        actions={actions}
        align={role === 'user' ? 'end' : 'start'}
        onClose={() => setMenu(null)}
      />
    )
  }

  const firstUser = messages.find(m => m.role === 'user')?.parts.find(p => p.type === 'text')
  const heading = threadTitle(title, firstUser?.type === 'text' ? firstUser.text : chat.asked) ?? 'Ask'

  const pending = loading || (threadId !== null && threadQ.isPending)
  const empty = !pending && messages.length === 0 && !chat.asked && chat.live.length === 0

  const liveParts = chat.live
  const caret = chat.streaming && liveParts[liveParts.length - 1]?.type === 'text'

  return (
    <div className="chat" ref={col} style={{ '--chat-composer-h': `${composerH}px` } as CSSProperties}>
      {/* WP 10.1 (Doug: the heading is cut off, large, and not worth the
          room): one line of body type, then New, Threads and the account
          button. The Worker keeps titles short (10.0); the ellipsis is the
          net for the ones that are not. */}
      <ScreenHead
        className="chat-head"
        lead={<h1 className="chat-head__title">{heading}</h1>}
        trailing={
          <>
            <IconButton label="New thread" onClick={onNew}><Plus aria-hidden="true" /></IconButton>
            <IconButton label="Threads" onClick={onThreads}><History aria-hidden="true" /></IconButton>
          </>
        }
      />

      {pending ? (
        <Skeleton variant="lines" />
      ) : listError || (!threadQ.data && threadQ.error) ? (
        // a2-04 — a read that failed is not an empty thread. (A refetch
        // that fails over messages already read keeps drawing them.)
        <Failed error={(listError ?? threadQ.error)!.message} />
      ) : empty ? (
        /* Doug, 29 Sept: no placeholder card on an empty thread. The composer's
           own placeholder says what to do; the page above it stays quiet. */
        starters?.length ? (
          <div className="chat-starters">
            {starters.map(q => (
              <button key={q} type="button" onClick={() => void send(q)} disabled={chat.streaming}>{q}</button>
            ))}
          </div>
        ) : null
      ) : (
        <div className="chat-log">
          {messages.map(m => (
            <div key={m.id} className={`chat-msg chat-msg--${m.role}`} data-menu={menu === m.id || undefined}>
              <span className="chat-stamp" aria-hidden="true">{stampFor(m.created_at, now)}</span>
              <Parts
                role={m.role} parts={m.parts ?? []} rows={rows} byEntry={byEntry}
                outcomes={overrides} onOutcome={onOutcome} onOpen={setOpen}
                onHold={() => setMenu(m.id)}
                thought={thoughts.get(m.id) ?? null}
                choicesOpen={openKey === m.id} onChoose={choose}
              />
              {menuFor(m.id, m.role, m.parts ?? [], m.created_at)}
            </div>
          ))}
          {chat.asked && (
            <div className="chat-msg chat-msg--user" data-menu={menu === LIVE_Q || undefined}>
              <Bubble role="user" text={chat.asked} onHold={chat.streaming ? undefined : () => setMenu(LIVE_Q)} />
              {menuFor(LIVE_Q, 'user', [{ type: 'text', text: chat.asked }], null)}
            </div>
          )}
          {liveAnswerShown && (
            <div className="chat-msg chat-msg--assistant" aria-busy={chat.streaming || undefined} data-menu={menu === LIVE_A || undefined}>
              <Parts
                role="assistant" parts={liveParts} rows={rows} byEntry={byEntry}
                outcomes={overrides} onOutcome={onOutcome} onOpen={setOpen}
                caret={caret}
                onHold={chat.streaming ? undefined : () => setMenu(LIVE_A)}
                thought={chat.thinking}
                answering={liveParts.some(p => p.type === 'text')}
                showTrail={!chat.streaming} trail={chat.trail}
                choicesOpen={openKey === LIVE_A} onChoose={choose}
              />
              <Status label={chat.status} />
              {/* a2-07 — Doug chose: say why, keep Ask again. Every way a
                  turn can fail is this one line now — the reason
                  (`failureFor`: "No connection", "Ask is down", the
                  stream's own message, "Interrupted"), and Ask again when
                  asking again could help. The stream's own errors used to
                  be a red banner with "Try again" and a dropped stream this
                  line; two looks and two verbs for the same next step. */}
              {chat.error && (
                <div className="chat-interrupted" role="status">
                  <p>{chat.error.message}</p>
                  {chat.error.retryable && (
                    <Button variant="secondary" size="sm" onClick={again} disabled={chat.streaming}>Ask again</Button>
                  )}
                </div>
              )}
              {menuFor(LIVE_A, 'assistant', liveParts, null)}
            </div>
          )}
        </div>
      )}

      <Composer
        streaming={chat.streaming} onSend={text => void send(text)} onStop={chat.stop} onHeight={setComposerH}
        seed={fill} placeholder={answerable ? 'Or type an answer…' : undefined}
      />
      <EventSheet e={open} onClose={() => setOpen(null)} />
    </div>
  )
}
